/**
 * chartParts.tsx — the DOM pieces both charts render the same way (§7.12):
 * the price flag with the candle timer, the watermark, the candle skeleton,
 * the order-line pill and the crosshair <style>. Markup lives here so the
 * terminal (uPlot) and the site (uPlot) cannot drift.
 */
import { crosshairCss, type ChartLook } from './chart';

/** Right-edge last-price flag: price over the bar-close countdown.
 *  `top` = y of the price line in px (the flag centres on it). */
/** `axisLeft` (px from the chart's left edge where the price axis starts):
 * the flag becomes a row OF the axis — same font, same left inset as the
 * tick labels, the price centred on its own level — instead of a sticker
 * on top of it (owner 08.10.2026). */
export function PriceFlag({ top, price, countdown, up, axisLeft }: { top: number; price: string; countdown?: string | null; up: boolean | null; axisLeft?: number }) {
  if (axisLeft != null) {
    return (
      <div className={`qt-priceflag qt-priceflag--axis ${up === false ? 'qt-priceflag--bear' : ''}`} style={{ top: top - 8, left: axisLeft }}>
        <div className="qt-priceflag__price">{price}</div>
        {countdown ? <div className="qt-priceflag__timer">{countdown}</div> : null}
      </div>
    );
  }
  return (
    <div className={`qt-priceflag ${up === false ? 'qt-priceflag--bear' : ''}`} style={{ top: top - (countdown ? 16 : 9) }}>
      <div className="qt-priceflag__price">{price}</div>
      {countdown ? <div className="qt-priceflag__timer">{countdown}</div> : null}
    </div>
  );
}

/** Colour of the dashed last-price line — same as the flag. */
export const priceFlagColor = (up: boolean | null) => (up === false ? 'var(--bear)' : 'var(--bull)');

export function ChartWatermark({ text }: { text: string }) {
  return <div aria-hidden className="qt-chart-watermark">{text}</div>;
}

export function ChartLookStyle({ look, colors, scope }: { look: ChartLook; colors: { mid: string; hi: string; acc: string }; scope: string }) {
  return <style>{crosshairCss(look, colors, scope)}</style>;
}

/** 24 skeleton candles along the bottom third + the caller's pill (children). */
export function ChartSkeleton({ children }: { children?: React.ReactNode }) {
  return (
    <>
      <div className="qt-chart-loading">
        {Array.from({ length: 24 }, (_, i) => <span key={i} className="qt-skel" style={{ height: `${8 + ((i * 37) % 23)}%` }} />)}
      </div>
      {children ? <div className="qt-chart-loading__pill">{children}</div> : null}
    </>
  );
}

/** Order-line pill at the right edge: `TP +312`, `SL −96`, `Entry 0.25`. */
export function OrderPill({ top, tone, children }: { top: number; tone: 'bull' | 'bear' | 'neutral'; children: React.ReactNode }) {
  return <div className={`qt-orderpill qt-orderpill--${tone}`} style={{ top: top - 9 }}>{children}</div>;
}

/** Stack right-edge tags (price flag, order pills) so none overlap. The tag with id
 *  `'last'` keeps its true y; the others slide away from it (and from each other).
 *  Returns id → centre y. `h` = tag height, `minGap` = space between tags. */
export function layoutAxisTags(tags: { id: string; y: number; h: number }[], minGap = 2): Record<string, number> {
  const out: Record<string, number> = {};
  const sorted = [...tags].sort((a, b) => a.y - b.y);
  const anchor = sorted.findIndex((t) => t.id === 'last');
  const place = (t: { id: string; y: number; h: number }, y: number) => { out[t.id] = y; };
  sorted.forEach((t, i) => { if (i !== anchor) place(t, t.y); });
  if (anchor >= 0) place(sorted[anchor], sorted[anchor].y);
  const gap = (a: { h: number }, b: { h: number }) => (a.h + b.h) / 2 + minGap;
  // push down from the anchor (or from the top when there is none)
  for (let i = Math.max(anchor, 0) + (anchor >= 0 ? 1 : 1); i < sorted.length; i++) {
    const p = sorted[i - 1], t = sorted[i];
    out[t.id] = Math.max(out[t.id], out[p.id] + gap(p, t));
  }
  // push up above the anchor
  for (let i = anchor - 1; i >= 0; i--) {
    const n = sorted[i + 1], t = sorted[i];
    out[t.id] = Math.min(out[t.id], out[n.id] - gap(t, n));
  }
  // no anchor: the first tag stayed put and the rest were pushed down — already done
  return out;
}
